const PREFIX = 'malka.jobs.submission.v1.';
const writes = new Map();
const cleanupListeners = new Set();
const removing = new Set();
let generation = 0;
let blocked = false;
let cleanup = null;
const failure = () => new Error('Private submission storage is unavailable.');
export function privateJobDraftGeneration() { return generation; }
export function subscribePrivateJobDraftCleanup(listener) {
    cleanupListeners.add(listener);
    return () => { cleanupListeners.delete(listener); };
}
function keychain() {
    if (typeof navigator === 'undefined' || navigator.product !== 'ReactNative')
        throw failure();
    try {
        // Kept lazy so browser bundles do not evaluate a native module.
        // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
        const module = require('react-native-keychain');
        if (!module.setGenericPassword || !module.getGenericPassword || !module.resetGenericPassword
            || !module.getAllGenericPasswordServices || !module.ACCESSIBLE)
            throw failure();
        return module;
    }
    catch {
        throw failure();
    }
}
function service(scope, operationId) {
    return PREFIX + encodeURIComponent(scope.origin) + ':' + encodeURIComponent(scope.interpreterId) + ':' + operationId;
}
async function ready() {
    if (cleanup)
        await cleanup;
    if (blocked)
        throw failure();
}
async function erase(module, name) {
    // A false reset may mean already absent. Confirm absence independently.
    await module.resetGenericPassword({ service: name });
    if (await module.getGenericPassword({ service: name }))
        throw failure();
}
export async function readPrivateJobSubmissionDraft(scope, operationId) {
    const current = generation;
    if (blocked || cleanup)
        throw failure();
    await ready();
    const name = service(scope, operationId);
    if (generation !== current || blocked || removing.has(name))
        throw failure();
    const saved = await keychain().getGenericPassword({ service: name });
    if (current !== generation || blocked || removing.has(name))
        throw failure();
    return saved ? saved.password : null;
}
/** Caller must first persist the non-sensitive operation marker. */
export async function writePrivateJobSubmissionDraft(scope, operationId, value) {
    const current = generation;
    await ready();
    const name = service(scope, operationId), module = keychain();
    if (generation !== current || blocked || removing.has(name) || writes.has(name))
        throw failure();
    const work = (async () => {
        try {
            const result = await module.setGenericPassword('submission', value, {
                service: name, accessible: module.ACCESSIBLE.WHEN_UNLOCKED_THIS_DEVICE_ONLY
            });
            if (!result)
                throw failure();
            const saved = await module.getGenericPassword({ service: name });
            if (!saved || saved.password !== value || generation !== current || blocked || removing.has(name))
                throw failure();
        }
        catch {
            // Also remove a partial write and a save that finishes after sign-out.
            await erase(module, name);
            throw failure();
        }
    })();
    writes.set(name, work);
    try {
        await work;
    }
    finally {
        if (writes.get(name) === work)
            writes.delete(name);
    }
}
export async function removePrivateJobSubmissionDraft(scope, operationId) {
    const name = service(scope, operationId);
    removing.add(name);
    // The save continuation observes this fence before it can become usable.
    await Promise.allSettled(writes.has(name) ? [writes.get(name)] : []);
    await erase(keychain(), name);
    removing.delete(name);
}
/**
 * Fence synchronously, then await every old write before removing only this
 * feature's services. Sign-out itself must continue even if native storage fails.
 * Non-sensitive server recovery markers deliberately remain with their account.
 */
export function clearPrivateJobSubmissionDrafts() {
    const current = ++generation, previous = cleanup;
    blocked = true;
    // Clear focused in-memory forms too; a failed UI callback cannot trap logout.
    for (const listener of cleanupListeners) {
        void Promise.resolve().then(() => { if (cleanupListeners.has(listener))
            listener(); })
            .catch(() => console.warn('Private submission view cleanup failed'));
    }
    const work = (async () => {
        if (previous)
            await Promise.allSettled([previous]);
        await Promise.allSettled(Array.from(writes.values()));
        const module = keychain();
        const names = (await module.getAllGenericPasswordServices()).filter(name => name.startsWith(PREFIX));
        await Promise.all(names.map(name => erase(module, name)));
        if (generation === current) {
            blocked = false;
            removing.clear();
        }
    })();
    cleanup = work;
    void work.then(() => { if (cleanup === work)
        cleanup = null; }, () => { if (cleanup === work)
        cleanup = null; });
    return work;
}
